import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Snackbar
} from '@mui/material';
import {
  Send as SendIcon,
  AccountCircle as AccountCircleIcon,
  VpnKey as VpnKeyIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { AuthContext } from '../utils/AuthContext';

// Encryption key (should be stored securely in production)
const ENCRYPTION_KEY = process.env.REACT_APP_SERVODESK_APP_KEY || 'V!z!0n$y$&$erv0deskHelpDe$k@pp20';

// Helper function to convert ArrayBuffer to Base64
const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// Encryption function using Web Crypto API
const encryptData = async (plaintext, key = ENCRYPTION_KEY) => {
  try {
    const iv = window.crypto.getRandomValues(new Uint8Array(16));
    const keyBytes = new TextEncoder().encode(key);
    const plaintextBytes = new TextEncoder().encode(plaintext);

    const cryptoKey = await window.crypto.subtle.importKey(
      "raw", 
      keyBytes, 
      { name: "AES-CBC" }, 
      false, 
      ["encrypt"]
    );

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: "AES-CBC", iv },
      cryptoKey,
      plaintextBytes
    );

    const encryptedBytes = new Uint8Array(encryptedBuffer);
    const combined = new Uint8Array(iv.length + encryptedBytes.length);
    combined.set(iv);
    combined.set(encryptedBytes, iv.length);

    return arrayBufferToBase64(combined);
  } catch (err) {
    console.error("Encryption failed:", err);
    throw new Error("Encryption failed");
  }
};

// Decryption function using Web Crypto API
const decryptData = async (ciphertext, key = ENCRYPTION_KEY) => {
  try {
    const data = Uint8Array.from(atob(ciphertext), (c) => c.charCodeAt(0));
    const iv = data.slice(0, 16);
    const encryptedBytes = data.slice(16);
    const keyBytes = new TextEncoder().encode(key);

    const cryptoKey = await window.crypto.subtle.importKey(
      "raw", 
      keyBytes, 
      { name: "AES-CBC" }, 
      false, 
      ["decrypt"]
    );

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-CBC", iv },
      cryptoKey,
      encryptedBytes
    );

    return new TextDecoder().decode(decryptedBuffer);
  } catch (err) {
    console.error("Decryption failed:", err);
    throw new Error("Decryption failed");
  }
};

// Configure axios interceptors
const configureAxiosInterceptors = () => {
  axios.interceptors.request.use(async (request) => {
    const token = localStorage.getItem("jwtToken");
    const tenantCode = localStorage.getItem("tenantCode");

    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }

    if (tenantCode) {
      request.headers["tenant-code"] = tenantCode;
      request.headers["app-code"] = "external";
      request.headers["app-type"] = "Web";
      request.headers["x-request-id"] = uuidv4();
    }

    if (request.data && request.headers['Content-Encrypted'] === 'true') {
      try {
        const payload = typeof request.data === 'string' ? request.data : JSON.stringify(request.data);
        request.data = await encryptData(payload);
      } catch (error) {
        console.error("Failed to encrypt request data:", error);
        throw error;
      }
    }

    return request;
  });

  axios.interceptors.response.use(
    async (response) => {
      if (response.data && response.config.headers['Content-Encrypted'] === 'true') {
        try {
          response.data = await decryptData(response.data);
          try {
            response.data = JSON.parse(response.data);
          } catch (e) {
            // Not JSON, keep as is
          }
        } catch (error) {
          console.error("Failed to decrypt response data:", error);
          throw error;
        }
      }
      return response;
    },
    (error) => {
      if (error.response) {
        console.error("API Error:", error.response.status, error.response.data);
      }
      return Promise.reject(error);
    }
  );
};

// Initialize axios configuration
configureAxiosInterceptors();

const PostmanLogin = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    tenantCode: 'vizionsys'
  });
  // const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const { myOrgName, setMyOrgName, token, setToken, tenantCode, setTenantCode,baseUrl,setBaseUrl } = useContext(AuthContext);
  const [error, setError] = useState('');
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [storedToken, setStoredToken] = useState('');
  const [storedTenantCode, setStoredTenantCode] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load stored credentials on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('jwtToken');
    const savedTenantCode = localStorage.getItem('tenantCode');

    if (savedToken) {
      setStoredToken(savedToken);
      setToken(savedToken);
    }

    if (savedTenantCode) {
      setStoredTenantCode(savedTenantCode);
      setCredentials(prev => ({ ...prev, tenantCode: savedTenantCode }));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${baseUrl}hii/gateway/common/Login`,
        {
          username: credentials.username,
          password: credentials.password,
          tenantcode: credentials.tenantCode
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Content-Encrypted': 'true',
            'X-Tenant-Code': credentials.tenantCode
          }
        }
      );

      const { token: responseToken } = response.data;

      localStorage.setItem('jwtToken', responseToken);
      localStorage.setItem('tenantCode', credentials.tenantCode);
      setStoredToken(responseToken);
      setStoredTenantCode(credentials.tenantCode);
      setToken(responseToken);

      setResponse({
        status: response.status,
        data: response.data
      });

      setLoginDialogOpen(false);
      showSnackbar('Login successful!', 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';
      setError(errorMessage);
      setResponse({
        status: err.response?.status || 500,
        data: { error: errorMessage }
      });
      showSnackbar(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreCredentials = () => {
    localStorage.setItem('jwtToken', token);
    localStorage.setItem('tenantCode', credentials.tenantCode);
    setStoredToken(token);
    setStoredTenantCode(credentials.tenantCode);
    showSnackbar('Credentials stored!', 'success');
  };

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('tenantCode');
    setStoredToken('');
    setStoredTenantCode('');
    setToken('');
    setResponse(null);
    showSnackbar('Logged out successfully', 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCopy = (text, message = 'Copied to clipboard!') => {
    navigator.clipboard.writeText(text);
    showSnackbar(message, 'info');
  };

  return (
    <Box sx={{
      width: '100%',
      maxWidth: 800,
      mx: 'auto',
      mt: 4,
      p: 3,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: 'background.paper'
    }}>
      <Typography variant="h5" gutterBottom>
        Secure API Credentials Manager
      </Typography>

      {storedToken ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <TextField
            fullWidth
            label="Token"
            variant="outlined"
            size="small"
            value={storedToken}
            InputProps={{
              readOnly: true,
              startAdornment: <LockIcon sx={{ color: 'action.active', mr: 1 }} />,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleCopy(storedToken, 'Token copied!')}
                    size="small"
                    edge="end"
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          <TextField
            fullWidth
            label="Tenant Code"
            variant="outlined"
            size="small"
            value={storedTenantCode}
            InputProps={{
              readOnly: true,
              startAdornment: <LockIcon sx={{ color: 'action.active', mr: 1 }} />,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleCopy(storedTenantCode, 'Tenant code copied!')}
                    size="small"
                    edge="end"
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          <Button
            variant="outlined"
            color="error"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{ height: '40px', minWidth: '100px' }}
          >
            Logout
          </Button>
        </Box>
      ) : (
        <>
          <TextField
            fullWidth
            label="Token"
            variant="outlined"
            margin="normal"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            InputProps={{
              startAdornment: <LockIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />

          <TextField
            fullWidth
            label="Tenant Code"
            variant="outlined"
            margin="normal"
            name="tenantCode"
            value={credentials.tenantCode}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: <LockIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleStoreCredentials}
              disabled={!token || !credentials.tenantCode}
            >
              Store Credentials
            </Button>

            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={() => setLoginDialogOpen(true)}
            >
              Login
            </Button>
          </Box>
        </>
      )}

      {/* Login Dialog */}
      <Dialog 
        open={loginDialogOpen} 
        onClose={() => setLoginDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Secure Login</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Username"
            variant="outlined"
            margin="normal"
            name="username"
            value={credentials.username}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: <AccountCircleIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            variant="outlined"
            margin="normal"
            name="password"
            value={credentials.password}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: <VpnKeyIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />

          <TextField
            fullWidth
            label="Tenant Code"
            variant="outlined"
            margin="normal"
            name="tenantCode"
            value={credentials.tenantCode}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: <LockIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleLogin}
            disabled={loading || !credentials.username || !credentials.password}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Response Section */}
      {response && (
        <Paper elevation={0} sx={{ mt: 3, p: 2, bgcolor: '#272822' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" color="white" gutterBottom>
              API Response (Status: {response.status})
            </Typography>
            <IconButton
              onClick={() => handleCopy(JSON.stringify(response.data, null, 2), 'Response copied!')}
              size="small"
              sx={{ color: 'white' }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <JSONPretty
            data={response.data}
            theme="monikai"
            style={{ fontSize: 14 }}
          />
        </Paper>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PostmanLogin;